import type { KnowledgeDoc, KnowledgeDocId, MockCache } from '../types';

const SHARE_TOKEN_MIN_LENGTH = 16;

function bySortKey(left: KnowledgeDoc, right: KnowledgeDoc): number {
  if (left.sortKey !== right.sortKey) {
    return left.sortKey < right.sortKey ? -1 : 1;
  }
  return left.id < right.id ? -1 : 1;
}

export function docChildren(cache: MockCache, parentId: KnowledgeDocId | null): KnowledgeDoc[] {
  const children: KnowledgeDoc[] = [];
  for (const doc of cache.docs.values()) {
    const inScope = parentId !== null || doc.workspaceId === cache.activeWorkspaceId;
    if (doc.parentId === parentId && doc.archivedAt === null && inScope) {
      children.push(doc);
    }
  }
  return children.sort(bySortKey);
}

export function docAncestors(cache: MockCache, id: KnowledgeDocId): KnowledgeDoc[] {
  const ancestors: KnowledgeDoc[] = [];
  const seen = new Set<KnowledgeDocId>([id]);
  let parentId = cache.docs.get(id)?.parentId ?? null;
  while (parentId !== null && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = cache.docs.get(parentId);
    if (parent === undefined) {
      break;
    }
    ancestors.unshift(parent);
    parentId = parent.parentId;
  }
  return ancestors;
}

export function docDepth(cache: MockCache, id: KnowledgeDocId): number {
  return cache.docs.has(id) ? docAncestors(cache, id).length + 1 : 0;
}

export function docSubtreeHeight(cache: MockCache, id: KnowledgeDocId): number {
  if (!cache.docs.has(id)) {
    return 0;
  }
  let deepest = 0;
  for (const child of docChildren(cache, id)) {
    deepest = Math.max(deepest, docSubtreeHeight(cache, child.id));
  }
  return deepest + 1;
}

export function docByShareToken(cache: MockCache, token: string): KnowledgeDoc | undefined {
  if (token.length < SHARE_TOKEN_MIN_LENGTH) {
    return undefined;
  }
  for (const doc of cache.docs.values()) {
    if (doc.shareToken === token && doc.archivedAt === null) {
      return doc;
    }
  }
  return undefined;
}

export function canEditDoc(cache: MockCache, id: KnowledgeDocId): boolean {
  const doc = cache.docs.get(id);
  return doc !== undefined && !doc.editLocked && doc.archivedAt === null;
}

export function isAdmin(cache: MockCache): boolean {
  return cache.users.get(cache.me)?.role === 'admin';
}

export function isManager(cache: MockCache): boolean {
  const role = cache.users.get(cache.me)?.role;
  return role === 'manager' || role === 'admin';
}

export function newShareToken(): string {
  return crypto.randomUUID().replaceAll('-', '');
}
